/**
 * AppGrid Component
 * 
 * Class component for Chrome 68 compatibility
 */

import React, { Component } from 'react';
import AppCard from './AppCard';

var LOAD_AHEAD_COUNT = (4 * 6); // trigger load when this many cards remain below viewport

function computeRowHeight(width) {
    var vw = width || 1024;
    var cardHeight = 235; // base for <=575px
    if (vw >= 767) {
        cardHeight = 300;
    } else if (vw >= 575) {
        cardHeight = 270;
    }
    // Wrapper padding matches CSS breakpoints (rem scaled to 16px)
    var paddingRem = 0.5;
    if (vw <= 576) {
        paddingRem = 0.25;
    } else if (vw <= 768) {
        paddingRem = 0.375;
    }
    var paddingPx = paddingRem * 16;
    return Math.round(cardHeight + (paddingPx * 2));
}

class AppGrid extends Component {
    constructor(props) {
        super(props);
        var initialWidth = (typeof window !== 'undefined' ? window.innerWidth : 1024);
        this.state = {
            width: initialWidth,
            columnCount: 1,
            rowHeight: computeRowHeight(initialWidth),
            scrollY: (typeof window !== 'undefined' ? window.pageYOffset : 0),
            viewportHeight: (typeof window !== 'undefined' ? window.innerHeight : 800)
        };
        this._onScroll = this._onScroll.bind(this);
        this._onResize = this._onResize.bind(this);
        this._checkLoadMore = this._checkLoadMore.bind(this);
        this.measureLayout = this.measureLayout.bind(this);
        this.containerRef = React.createRef();
        this._heightTrim = 0;
        this._lastScrollY = this.state.scrollY || 0;
    }

    componentDidMount() {
        if (typeof window !== 'undefined') {
            window.addEventListener('scroll', this._onScroll, { passive: true });
            window.addEventListener('resize', this._onResize);
            var self = this;
            setTimeout(function() { self.measureLayout(); }, 0);
            setTimeout(function() { self._checkLoadMore(); }, 0);
        }
    }
    componentWillUnmount() {
        if (typeof window !== 'undefined') {
            window.removeEventListener('scroll', this._onScroll);
            window.removeEventListener('resize', this._onResize);
        }
        this._scheduledLoad = false;
        this._unmounted = true;
    }

    componentDidUpdate(prevProps) {
        if (prevProps.apps !== this.props.apps) {
            var self = this;
            setTimeout(function() { self.measureLayout(); }, 50);
        }
        if (prevProps.apps !== this.props.apps || prevProps.isLoading !== this.props.isLoading || prevProps.hasMore !== this.props.hasMore) {
            this._checkLoadMore();
        }
    }

    _onResize() {
        if (typeof window === 'undefined') return;
        var w = window.innerWidth;
        var vh = window.innerHeight;
        this.setState({ width: w, viewportHeight: vh });
        var self = this;
        setTimeout(function() { self.measureLayout(); }, 50);
    }

    _onScroll() {
        var newY = window.pageYOffset || document.documentElement.scrollTop || 0;
        var prevY = this._lastScrollY || 0;
        var delta = newY - prevY;
        this._lastScrollY = newY;

        if (delta < 0) {
            // Scrolling up: gradually trim the virtual height (but keep a safe minimum)
            var width = this.state.width || (typeof window !== 'undefined' ? window.innerWidth : 1024);
            var rowH = this.state.rowHeight || computeRowHeight(width);
            var loaded = (this.props.apps && this.props.apps.length) ? this.props.apps.length : 0;
            var cols = this.state.columnCount || 1;
            var rows = Math.max(1, Math.ceil(loaded / cols));
            var totalHeight = rows * rowH;
            var minHeight = Math.max(this.state.viewportHeight || 800, rowH * 4);
            var maxTrim = Math.max(0, totalHeight - minHeight);
            var increment = Math.min(Math.abs(delta), rowH);
            this._heightTrim = Math.min(maxTrim, (this._heightTrim || 0) + increment);
        } else if (delta > 0) {
            // Scrolling down: restore full height
            this._heightTrim = 0;
        }

        this.setState({ scrollY: newY }, this._checkLoadMore);
    }

    measureLayout() {
        try {
            if (!this.containerRef || !this.containerRef.current) return;
            var wrapper = this.containerRef.current.querySelector('.bb-app-card-wrapper');
            if (!wrapper) return;
            var gridWidth = this.containerRef.current.getBoundingClientRect ? this.containerRef.current.getBoundingClientRect().width : this.containerRef.current.offsetWidth;
            var cardRect = wrapper.getBoundingClientRect ? wrapper.getBoundingClientRect() : null;
            var cardWidth = cardRect ? cardRect.width : (wrapper.offsetWidth || 0);
            var wrapperHeight = wrapper.getBoundingClientRect ? wrapper.getBoundingClientRect().height : (wrapper.offsetHeight || 0);
            var cols = cardWidth ? Math.max(1, Math.floor(gridWidth / cardWidth)) : 1;
            var expectedRowHeight = computeRowHeight(this.state.width || gridWidth);
            var desiredRowHeight = wrapperHeight ? Math.max(expectedRowHeight, Math.round(wrapperHeight)) : expectedRowHeight;
            var nextState = {};
            var changed = false;
            if (cols !== this.state.columnCount) {
                nextState.columnCount = cols;
                changed = true;
            }
            if (desiredRowHeight !== this.state.rowHeight) {
                nextState.rowHeight = desiredRowHeight;
                changed = true;
            }
            if (changed) {
                this.setState(nextState);
            }
        } catch (e) { }
    }

    _checkLoadMore() {
        if (this._unmounted) return;
        if (!this.props || !this.props.hasMore || this.props.isLoading || typeof this.props.onLoadMore !== 'function') {
            return;
        }
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return;
        }
        var columnCount = this.state.columnCount || 1;
        var rowHeight = this.state.rowHeight || computeRowHeight(this.state.width);
        var loaded = (this.props.apps && this.props.apps.length) ? this.props.apps.length : 0;
        if (!loaded) return;
        var rowCount = Math.max(1, Math.ceil(loaded / columnCount));
        var scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
        var vh = window.innerHeight || document.documentElement.clientHeight || 0;
        var containerTop = 0;
        if (this.containerRef && this.containerRef.current) {
            var rect = this.containerRef.current.getBoundingClientRect();
            containerTop = (window.pageYOffset || document.documentElement.scrollTop || 0) + rect.top;
        }
        var gridBottom = containerTop + (rowCount * rowHeight);
        var remainingPx = gridBottom - (scrollY + vh);
        var remainingRows = Math.ceil(remainingPx / rowHeight);
        if (remainingRows < 0) remainingRows = 0;
        var remainingCards = remainingRows * columnCount;
        if (remainingCards <= LOAD_AHEAD_COUNT && !this._scheduledLoad) {
            this._scheduledLoad = true;
            var self = this;
            setTimeout(function() {
                if (self._unmounted) {
                    self._scheduledLoad = false;
                    return;
                }
                try {
                    self.props.onLoadMore && self.props.onLoadMore();
                } catch (e) {
                    try { console.error('onLoadMore error', e && e.message); } catch (er) {}
                }
                self._scheduledLoad = false;
            }, 50);
        }
    }

    render() {
        var props = this.props;

        // Loading / error / empty states
        if (props.isLoading && (!props.apps || props.apps.length === 0)) {
            return React.createElement('div', { className: 'bb-loading' },
                React.createElement('div', { className: 'bb-spinner' }),
                React.createElement('p', null, 'Loading apps...')
            );
        }
        if (props.error) {
            return React.createElement('div', { className: 'bb-empty' },
                React.createElement('div', { className: 'bb-empty-icon' }, '⚠️'),
                React.createElement('h3', null, 'Error Loading Apps'),
                React.createElement('p', null, props.error)
            );
        }
        var apps = props.apps || [];
        if (!apps || apps.length === 0) {
            return React.createElement('div', { className: 'bb-empty' },
                React.createElement('div', { className: 'bb-empty-icon' }, '📭'),
                React.createElement('h3', null, 'No Apps Found'),
                React.createElement('p', null, 'Try adjusting your search or filters.')
            );
        }

        var loadedCount = apps.length;
        var columnCount = this.state.columnCount || 1;
        var rowHeight = this.state.rowHeight || computeRowHeight(this.state.width);
        var rowCount = Math.max(1, Math.ceil(loadedCount / columnCount));
        var totalHeight = rowCount * rowHeight;

        var containerTop = 0;
        if (this.containerRef && this.containerRef.current) {
            var rect = this.containerRef.current.getBoundingClientRect();
            containerTop = (window.pageYOffset || document.documentElement.scrollTop || 0) + rect.top;
        }

        var scrollY = this.state.scrollY || 0;
        var vh = this.state.viewportHeight || 800;
        var startRow = Math.floor(Math.max(0, (scrollY - containerTop) / rowHeight));
        var endRow = Math.ceil((scrollY - containerTop + vh) / rowHeight);
        var overscan = 3;
        startRow = Math.max(0, startRow - overscan);
        endRow = Math.min(rowCount - 1, endRow + overscan);

        var rows = [];
        for (var r = startRow; r <= endRow; r++) {
            var cols = [];
            var startIndex = r * columnCount;
            for (var c = 0; c < columnCount; c++) {
                var idx = startIndex + c;
                var item = apps[idx] || null;
                if (item) {
                    var up = item.upvotes || 0;
                    var down = item.downvotes || 0;
                    if (down - up >= 20) {
                        cols.push(React.createElement('div', { key: 'empty-'+idx, className: 'bb-app-card-wrapper' }));
                    } else {
                        var cardEl = React.createElement(AppCard, {
                            app: item,
                            voteData: item.voteData,
                            onVendorClick: props.onVendorClick,
                            onCategoryClick: props.onCategoryClick,
                            onVote: props.onVote,
                            voteLookup: props.voteLookup,
                            user: props.user,
                            onOpenComments: props.onOpenComments,
                            commentCount: item.comment_count || 0,
                            commentState: props.commentData ? props.commentData[item.package_name] : null
                        });
                        var cardKey = item.package_name || ('app-' + idx);
                        cols.push(React.createElement('div', { key: cardKey, className: 'bb-app-card-wrapper' }, cardEl));
                    }
                } else {
                    cols.push(React.createElement('div', { key: 'empty-'+idx, className: 'bb-app-card-wrapper' }));
                }
            }
            rows.push(React.createElement('div', { key: 'row-' + r, className: 'bb-app-row', style: { top: (r * rowHeight) + 'px' } }, cols));
        }

        var minHeight = Math.max(this.state.viewportHeight || 800, rowHeight * 4);
        var maxTrim = Math.max(0, totalHeight - minHeight);
        var appliedTrim = Math.min(this._heightTrim || 0, maxTrim);
        var innerHeight = Math.max(minHeight, totalHeight - appliedTrim);

        return React.createElement('div', { ref: this.containerRef, className: 'bb-app-grid', style: { position: 'relative' } },
            props.isLoading ? React.createElement('div', { className: 'bb-loading-overlay' }, React.createElement('div', { className: 'bb-spinner' })) : null,
            React.createElement('div', { className: 'bb-app-grid-inner', style: { height: innerHeight + 'px' } }, rows)
        );
    }

}

export default AppGrid;
